import { useCallback, useEffect, useMemo, useState } from 'react';
import useAuth from 'hooks/useAuth';
import { useSnackbar } from 'notistack';
import Layout from '../../../layouts';
import Page from '../../../components/Page';
import axios from '../../../utils/axios';
import { Num, PillStatus } from '../../../components/gps';
import PainelLista, { Coluna } from '../../../components/gps/PainelLista';
import MenuAcoes from '../../../components/gps/MenuAcoes';
import ModalRegional from '../../../components/gps/ModalRegional';
import { linhasIniciais } from '../../../components/gps/paginacao';

/* ============================================================================
   Regionais - visual novo (shadcn + Tailwind).

   Contrato conferido em remoteAPI/src/app/regionais/regionais.service.ts:382:
     GET /regionais/paginado?page&rows&valor&campo&direcao&ordenar&gerente&comAcesso&acesso
     -> content: [{ ...RegionaisEntity, status: "ATIVO"|"INATIVO",
                    podeEditar, statusSolicitacao, podePedirAcesso }]

   Os tres ultimos campos SO EXISTEM quando `comAcesso=true` - por isso o
   parametro e fixo aqui. Eles sao a razao de esta tela ser diferente das outras:
   ela lista tambem o que o usuario NAO pode acessar, para ele poder pedir.

   ESCOLHA DE DESENHO: em vez do filtro "todas / com acesso / sem acesso" ser um
   select escondido, virou segmentado com contagem - e assim que se ve, sem
   clicar, que existem regionais fora do seu alcance. Sem isso o usuario nao
   descobre que precisa pedir acesso; ele so ve uma lista curta e conclui que o
   resto nao existe.

   A UNICA ESCRITA desta tela e "Pedir acesso" (POST .../solicitar-acesso), que
   so dispara em clique explicito. Criar e editar regional seguem na pagina
   existente (/dashboard/regionais/new/:codigo).
   ============================================================================ */

type Regional = {
  codigo?: number;
  nome?: string;
  descricao?: string;
  status?: number | string;
  visivelTodos?: boolean;
  podeEditar?: boolean;
  podePedirAcesso?: boolean;
  statusSolicitacao?: string | null;
};

const ACESSO = { TODAS: 0, COM: 1, SEM: 2 } as const;
const NOME_ACESSO: Record<number, string> = { 0: 'todos', 1: 'com', 2: 'sem' };

function numero(valor: unknown) {
  const n = Number(valor);
  return Number.isFinite(n) ? n : 0;
}

function ehAtivo(v: unknown) {
  return String(v).toUpperCase() === 'ATIVO' || Number(v) === 1;
}

Regionais.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout>{page}</Layout>;
};

export default function Regionais() {
  const { user } = useAuth();

  const { enqueueSnackbar } = useSnackbar();
  const [linhas, setLinhas] = useState<Regional[]>([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(0);
  const [busca, setBusca] = useState('');
  const [acesso, setAcesso] = useState<number>(ACESSO.TODAS);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [pedindo, setPedindo] = useState<number | null>(null);
  const [menuAberto, setMenuAberto] = useState<number | null>(null);
  // `undefined` = fechado; '0' = criar; qualquer outro = editar aquele codigo.
  const [editando, setEditando] = useState<string | undefined>(undefined);
  const [linhasPorPagina, setLinhasPorPagina] = useState(linhasIniciais);
  // Vira propriedade do QueryBuilder (`regionais.${ordenar}`).
  const [ordenarPor, setOrdenarPor] = useState('nome');
  const [direcao, setDirecao] = useState<'asc' | 'desc'>('asc');
  const [indices, setIndices] = useState({ total: 0, ativos: 0, inativos: 0 });

  const carregar = useCallback(async () => {
    if (!user?.codigo) return;
    setCarregando(true);
    setErro('');
    try {
      const resposta = await axios.get<{ content: Regional[]; totalElements: number }>(
        `/regionais/paginado?page=${pagina}&rows=${linhasPorPagina}` +
          `&valor=${encodeURIComponent(busca.trim())}&campo=nome&direcao=${direcao}&ordenar=${ordenarPor}` +
          `&gerente=${user.codigo}&comAcesso=true&acesso=${NOME_ACESSO[acesso]}`
      );
      setLinhas(resposta.data?.content || []);
      setTotal(numero(resposta.data?.totalElements));
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível carregar as regionais.');
      setLinhas([]);
      setTotal(0); // sem isto o rodape seguia "Mostrando 0 de N" com a paginacao ativa
    } finally {
      setCarregando(false);
    }
  }, [pagina, busca, acesso, user?.codigo, linhasPorPagina, ordenarPor, direcao]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    axios
      .get<any>('/regionais/findByStatus')
      .then((r) => {
        const d = r.data || {};
        setIndices({ total: numero(d.total), ativos: numero(d.ativo), inativos: numero(d.inativo) });
      })
      .catch(() => setIndices({ total: 0, ativos: 0, inativos: 0 }));
  }, []);

  const pedirAcesso = useCallback(
    async (regional: Regional) => {
      if (!regional.codigo || pedindo) return;
      setPedindo(regional.codigo);
      try {
        await axios.post(`/regionais/${regional.codigo}/solicitar-acesso`);
        // Recarrega em vez de mexer no estado local: quem decide o proximo
        // rotulo da linha e o backend (`statusSolicitacao`), nao esta tela.
        await carregar();
      } catch (e: any) {
        // Snackbar, nao `setErro`: `erro` e o estado de CARGA da lista - usa-lo
        // aqui trocava a tabela inteira pela mensagem, e "tentar de novo" so
        // recarregava a lista, sem repetir o pedido.
        enqueueSnackbar(e?.response?.data?.message || 'Não foi possível solicitar acesso.', { variant: 'error' });
      } finally {
        setPedindo(null);
      }
    },
    [carregar, pedindo, enqueueSnackbar]
  );

  const colunas: Array<Coluna<Regional>> = useMemo(
    () => [
      {
        chave: 'nome',
        ordenarPor: 'nome',
        exportar: (r) => r.nome ?? '',
        rotulo: 'Regional',
        largura: 'minmax(200px,2.2fr)',
        esqueleto: 'w-40',
        render: (r) => (
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="block truncate text-[12.5px] font-semibold">{r.nome || ' - '}</span>
          </div>
        ),
      },
      {
        chave: 'descricao',
        ordenarPor: 'descricao',
        exportar: (r) => r.descricao ?? '',
        rotulo: 'Descrição',
        largura: 'minmax(180px,2fr)',
        esqueleto: 'w-48',
        render: (r) => (
          <span className="block truncate text-[12px] text-muted-foreground">{r.descricao || '—'}</span>
        ),
      },
      {
        chave: 'visibilidade',
        ordenarPor: 'visivelTodos',
        exportar: (r) => (r.visivelTodos ? 'todos veem' : 'restrita'),
        rotulo: 'Visibilidade',
        largura: '130px',
        esqueleto: 'w-20',
        render: (r) => (
          <span className="text-[12px] text-muted-foreground">
            {r.visivelTodos ? 'todos veem' : 'restrita'}
          </span>
        ),
      },
      {
        chave: 'acesso',
        exportar: (r) => (r.podeEditar ? 'com acesso' : r.statusSolicitacao ? 'pedido ' + r.statusSolicitacao : 'sem acesso'),
        rotulo: 'Seu acesso',
        largura: '170px',
        esqueleto: 'w-24',
        render: (r) => {
          if (r.podeEditar) {
            return (
              <span className="inline-flex h-[22px] items-center gap-1.5 rounded-md bg-ok-soft px-2 text-[11px] font-bold tracking-wide text-ok">
                <span className="h-1.5 w-1.5 rounded-full bg-current" />
                COM ACESSO
              </span>
            );
          }
          if (r.statusSolicitacao) {
            return (
              <span className="text-[12px] font-semibold text-muted-foreground">
                pedido {String(r.statusSolicitacao).toLowerCase()}
              </span>
            );
          }
          if (r.podePedirAcesso) {
            return (
              <button
                type="button"
                onClick={(e) => {
                  // A linha inteira navega para a edicao; sem isto, pedir acesso
                  // abriria o cadastro junto.
                  e.stopPropagation();
                  pedirAcesso(r);
                }}
                disabled={pedindo === r.codigo}
                className="h-7 rounded-md border border-line px-2.5 text-[12px] font-bold text-primary disabled:opacity-40"
              >
                {pedindo === r.codigo ? 'Enviando...' : 'Pedir acesso'}
              </button>
            );
          }
          return <span className="text-[12px] text-faint">sem acesso</span>;
        },
      },
      {
        chave: 'status',
        ordenarPor: 'status',
        exportar: (r) => (ehAtivo(r.status) ? 'ATIVA' : 'INATIVA'),
        rotulo: 'Status',
        largura: '110px',
        esqueleto: 'w-16',
        render: (r) => <PillStatus ativo={ehAtivo(r.status)} />,
      },
      {
        chave: 'acao',
        rotulo: 'Opções',
        largura: '80px',
        alinhar: 'dir',
        esqueleto: 'nenhum',
        // Regionais nao tem "Apagar" - a tela antiga tambem nao tinha no menu.
        // As acoes sao abrir/editar (so com acesso) e pedir acesso (so sem).
        render: (r) => (
          <MenuAcoes<Regional>
            linha={r}
            aberto={menuAberto === r.codigo}
            onAlternar={() => setMenuAberto(menuAberto === r.codigo ? null : r.codigo ?? null)}
            onFechar={() => setMenuAberto(null)}
            ancorarADireita
            acoes={[
              {
                rotulo: 'Abrir / editar',
                visivel: Boolean(r.podeEditar),
                ao: (x) => x.codigo && setEditando(String(x.codigo)),
              },
              {
                rotulo: 'Pedir acesso',
                visivel: Boolean(r.podePedirAcesso),
                ao: (x) => pedirAcesso(x),
              },
            ]}
          />
        ),
      },
    ],
    [pedirAcesso, pedindo, menuAberto]
  );

  return (
    <Page title="Regionais">
      <PainelLista<Regional>
        titulo="Regionais"
        descricao={
          <>
            A lista inclui regionais que você <strong>não</strong> acessa — é por ela que se pede acesso.
            Sem regional vinculada, um usuário não enxerga grupo, dispositivo nem política.
          </>
        }
        acao={
          <button
            type="button"
            onClick={() => setEditando('0')}
            className="h-9 flex-none rounded-lg bg-primary px-4 text-[12.5px] font-bold text-primary-foreground"
          >
            + Nova regional
          </button>
        }
        indicadores={[
          { rotulo: 'Total', valor: indices.total || total, legenda: 'cadastradas', tom: 'info' },
          {
            rotulo: 'Ativas',
            valor: indices.ativos,
            legenda: 'em uso',
            tom: 'ok',
            base: indices.total || total,
          },
          {
            rotulo: 'Inativas',
            valor: indices.inativos,
            legenda: 'desativadas',
            tom: 'danger',
            base: indices.total || total,
          },
        ]}
        busca={busca}
        aoBuscar={(v) => {
          setPagina(0);
          setBusca(v);
        }}
        placeholderBusca="Nome da regional"
        segmentos={[
          { valor: ACESSO.TODAS as number, rotulo: 'Todas' },
          { valor: ACESSO.COM as number, rotulo: 'Com acesso' },
          { valor: ACESSO.SEM as number, rotulo: 'Sem acesso' },
        ]}
        segmentoAtual={acesso}
        aoTrocarSegmento={(v) => {
          setPagina(0);
          setAcesso(v);
        }}
        ordenarPor={ordenarPor}
        direcao={direcao}
        aoOrdenar={(campo) => {
          if (campo === ordenarPor) setDirecao((v) => (v === 'asc' ? 'desc' : 'asc'));
          else { setOrdenarPor(campo); setDirecao('asc'); }
          setPagina(0);
        }}
        linhasPorPagina={linhasPorPagina}
        aoTrocarLinhasPorPagina={(n) => { setPagina(0); setLinhasPorPagina(n); }}
        aoLimparFiltros={() => { setBusca(''); setAcesso(ACESSO.TODAS); setOrdenarPor('nome'); setDirecao('asc'); setPagina(0); }}
        nomeArquivo="regionais"
        colunas={colunas}
        linhas={linhas}
        chaveLinha={(r, i) => String(r.codigo ?? `sem-codigo-${i}`)}
        aoClicarLinha={(r) =>
          // Sem acesso nao abre o cadastro: o backend recusaria o salvamento e o
          // usuario teria preenchido um formulario para nada.
          // Modal desta tela, nao a pagina antiga.
          r.podeEditar && r.codigo ? setEditando(String(r.codigo)) : undefined
        }
        carregando={carregando}
        erro={erro}
        aoRecarregar={carregar}
        vazio="Nenhuma regional encontrada com esse filtro."
        pagina={pagina}
        totalPaginas={Math.max(1, Math.ceil(total / linhasPorPagina))}
        total={total}
        aoTrocarPagina={setPagina}
        rotuloPlural="regionais"
      />

      <ModalRegional
        aberto={editando !== undefined}
        codigo={editando}
        onFechar={() => setEditando(undefined)}
        onSalvo={carregar}
      />
    </Page>
  );
}
